<script>
export default {
  name: 'AppAccordion',
  props: {
    title: {
      type: String,
      default: ''
    },
    iconSrc: {
      type: String,
      default: ''
    },
    expanded: {
      type: Boolean,
      default: false
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    checkboxValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: this.expanded,
      isChecked: this.checkboxValue
    };
  },
  watch: {
    expanded(newVal) {
      this.isExpanded = newVal;
    },
    checkboxValue(newVal) {
      this.isChecked = newVal;
    }
  },
  computed: {
    hasContent() {
      return !!this.$slots.default;
    },
    accordionClasses() {
      return {
        'app-accordion p-3 rounded-lg w-full': true,
        'accordion-checked': this.isChecked,
        'accordion-unchecked': !this.isChecked
      };
    },
    iconUrl() {
      return this.iconSrc || '';
    },
    iconType() {
      if (this.iconUrl.includes('dedication')) return 'accordion-icon-dedication';
      if (this.iconUrl.includes('comment')) return 'accordion-icon-comment';
      if (this.iconUrl.includes('anonymous')) return 'accordion-icon-anonymous';
      if (this.iconUrl.includes('company')) return 'accordion-icon-company';
      return '';
    }
  },
  methods: {
    onHeaderClick() {
      if (!this.hasContent && !this.showCheckbox) return;
      this.toggleState();
    },
    onCheckboxChange(checked) {
      // Sync state with checkbox value and behave like header click
      this.isChecked = checked;
      if (this.hasContent) {
        this.isExpanded = checked;
      }
      this.$emit('checkbox-change', this.isChecked);
      this.$emit('toggle', this.isExpanded);
    },
    toggleState() {
      // Toggle both checkbox and expanded state together
      this.isChecked = !this.isChecked;
      if (this.hasContent) {
        this.isExpanded = this.isChecked;
      }

      this.$emit('checkbox-change', this.isChecked);
      this.$emit('toggle', this.isExpanded);
    },
    // Transition hooks for smooth height animation
    onEnter(el) {
      el.style.height = '0';
      el.offsetHeight; // Force reflow
      el.style.height = el.scrollHeight + 'px';
    },
    onAfterEnter(el) {
      el.style.height = 'auto';
    },
    onLeave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.offsetHeight; // Force reflow
      el.style.height = '0';
    }
  }
};
</script>

<template>
  <div :class="accordionClasses">
    <!-- Header -->
    <div class="accordion-header flex items-center gap-2" :class="{ 'cursor-pointer': hasContent || showCheckbox }" @click="onHeaderClick">
      <!-- Icon -->
      <span v-if="iconSrc" class="accordion-icon" :class="{ 'accordion-icon-has-active': iconType }" aria-hidden="true">
        <img :src="iconUrl" class="accordion-icon-image accordion-icon-image-default" alt="" />
        <span v-if="iconType" class="accordion-icon-image accordion-icon-image-active" :class="iconType"></span>
      </span>

      <!-- Title -->
      <h3 class="text-base leading-4.5 font-bold text-black flex-1 m-0">
        <slot name="title">{{ title }}</slot>
      </h3>

      <!-- Checkbox (always shown if enabled) or Chevron (only if there's content) -->
      <div v-if="showCheckbox" class="accordion-checkbox" @click.stop>
        <app-checkbox :value="isChecked" :size="16" variant="accordion" @input="onCheckboxChange" />
      </div>
      <div v-else-if="hasContent" class="accordion-chevron duration-200" :class="{ 'rotate-180': isExpanded }">
        <ion-icon name="chevron-down"></ion-icon>
      </div>
    </div>

    <!-- Expanded Content -->
    <transition name="accordion-slide" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="isExpanded" class="accordion-content-wrapper">
        <div class="accordion-content">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>
